import { el } from "../lib/dom.js";
import { icon, type IconName } from "../lib/icons.js";

/**
 * The compact actions menu a row's ellipsis opens (g18), and the ellipsis
 * that opens it.
 *
 * A native popover, so the top layer, Escape and the click outside are the
 * browser's: `popover="auto"` gives light dismiss without a document listener
 * that has to know about every other surface, and the top layer puts the menu
 * over a scrolling column without a `z-index` contest. What is this file's is
 * the menu semantics — one `role="menu"` of `menuitem` buttons, and a
 * `menuitemcheckbox` for a setting that is on or off (g81), with a roving
 * tabindex, arrows that wrap, Home and End, Tab closing — and the order of
 * closing and acting: an entry's action runs *after* the menu has gone and
 * the focus is back on the anchor, so whatever the action opens next can take
 * the focus and keep it.
 *
 * One menu per screen, appended to the screen and never to a row: rows are
 * re-inserted when they move and rebuilt when a projection redraws, and a
 * popover pulled out of the document leaves the top layer without a word —
 * no `beforetoggle`, so the anchor would be left saying it is expanded. The
 * screen opens it for whichever row's ellipsis was pressed.
 *
 * Placed from the anchor's rectangle rather than CSS anchor positioning, which
 * not every engine this runs in has. The UA sheet centres a popover; the
 * stylesheet undoes that and this sets `top` and `left`.
 */

export interface MenuEntry {
  label: string;
  icon: IconName;
  onSelect(): void;
  /**
   * Said rather than hidden: an entry that is not available now — Move up on
   * the first row — stays in the menu, focusable and `aria-disabled`, so the
   * person hears that it exists and why nothing happened.
   */
  disabled?: boolean;
  /** Quiet red text for the destructive entry, never a filled button. */
  danger?: boolean;
  /**
   * Given at all, the entry is a setting that is on or off (g81): a
   * `menuitemcheckbox` that says which, and shows a tick while it is on.
   * Choosing it runs `onSelect`, which flips the setting.
   */
  checked?: boolean;
}

/** Every entry of a menu that can be chosen: plain ones and checkable ones alike. */
export const MENU_ENTRIES = '[role="menuitem"], [role="menuitemcheckbox"]';

/**
 * A line of words in the menu that is not a choice (g72: "Adding as Alex" on
 * a shared list). Never focused and never an item: the arrow keys pass it,
 * and it is presentation to anything that reads the menu's items.
 */
export interface MenuCaption {
  caption: string;
}

export const SEPARATOR = "separator";
export type MenuItem = MenuEntry | MenuCaption | typeof SEPARATOR;

/** Whether an item is something to choose. */
export function isEntry(item: MenuItem): item is MenuEntry {
  return item !== SEPARATOR && "onSelect" in item;
}

export interface ActionMenu {
  node: HTMLElement;
  /**
   * Opens beside `anchor` with what `entries` returns now. Open already for
   * this anchor, it closes instead — the ellipsis toggles. Open for another,
   * it moves. `entries` is kept and re-read by `refresh()`.
   */
  open(anchor: HTMLButtonElement, entries: () => MenuItem[]): void;
  close(): void;
  /**
   * The list changed under an open menu: the entries are re-read so an end
   * that stopped being one is re-enabled, and the menu is re-placed against
   * an anchor that may have moved. The focus stays on the entry it was on.
   */
  refresh(): void;
  isOpen(): boolean;
  anchor(): HTMLButtonElement | undefined;
}

/**
 * A length the product layer owns, in pixels: `--menu-offset` between the
 * menu and its anchor, `--menu-edge` between the menu and the viewport's
 * edge (g54). Read at each placement. The fallback only stands in for a
 * document with no stylesheet, such as a unit test's.
 */
function token(name: string, fallback: number): number {
  const value = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
  return Number.isFinite(value) ? value : fallback;
}

/**
 * The ellipsis. The screen names it — `Actions for {text}` — and keeps the
 * name current, because the text is the person's and changes.
 */
export function actionsButton(): HTMLButtonElement {
  return el("button", {
    class: "item-actions",
    attrs: { type: "button", "aria-haspopup": "menu", "aria-expanded": "false" },
    children: [icon("more")],
  });
}

let nextMenuId = 0;

export function actionMenu(): ActionMenu {
  const node = el("div", { class: "action-menu", attrs: { id: `action-menu-${++nextMenuId}`, popover: "auto", role: "menu" } });

  let current: { anchor: HTMLButtonElement; entries: () => MenuItem[] } | undefined;
  /**
   * Set by a press on the open menu's own anchor, and spent by the click that
   * follows. A browser without `showPopover({ source })` light-dismisses on
   * that press and then delivers the click, which would reopen the menu the
   * press just closed. One with it leaves the menu open through the press,
   * and the click then closes it, as a toggle should.
   *
   * Let go a task after the pointer lifts or the gesture is cancelled: a
   * press a scroll takes over, or a finger that leaves the ellipsis before
   * lifting, sends no click, and a guard spent only by the next open would
   * eat the next press on the anchor. A task later, not at once, because
   * the click of the same gesture is dispatched with the pointerup, before
   * any timer, and is the click the guard is for.
   */
  let pressedAnchor = false;
  let releasing: ReturnType<typeof setTimeout> | undefined;

  const items = () => [...node.querySelectorAll<HTMLButtonElement>(MENU_ENTRIES)];

  function focusItem(at: number): void {
    const all = items();
    const target = all[at];
    if (!target) return;
    for (const item of all) item.tabIndex = item === target ? 0 : -1;
    target.focus({ preventScroll: true });
  }

  function paint(entries: MenuItem[]): void {
    node.replaceChildren(
      ...entries.map((entry) =>
        entry === SEPARATOR
          ? el("div", { attrs: { role: "separator" } })
          : !isEntry(entry)
          ? el("p", { class: "menu-caption", attrs: { role: "presentation" }, text: entry.caption })
          : el("button", {
              class: entry.danger ? "menu-danger" : undefined,
              attrs: {
                type: "button",
                role: entry.checked === undefined ? "menuitem" : "menuitemcheckbox",
                // String-valued for the reason below: off is said, not left out.
                ...(entry.checked === undefined ? {} : { "aria-checked": String(entry.checked) }),
                tabindex: -1,
                // String-valued on purpose: `el()` drops `false`, and a
                // menuitem that says nothing about being disabled is read
                // as enabled — which is right, but says it explicitly.
                "aria-disabled": String(Boolean(entry.disabled)),
              },
              children: [
                icon(entry.icon),
                el("span", { text: entry.label }),
                // The tick is the state for the eye; `aria-checked` says it to everything else.
                ...(entry.checked ? [el("span", { class: "menu-check", attrs: { "aria-hidden": "true" }, children: [icon("check")] })] : []),
              ],
              on: {
                click: () => {
                  if (entry.disabled) return;
                  // Closed first, then the action: see the file comment.
                  close();
                  entry.onSelect();
                },
              },
            }),
      ),
    );
  }

  function place(anchor: HTMLButtonElement): void {
    const at = anchor.getBoundingClientRect();
    const size = node.getBoundingClientRect();
    // Right-aligned under the ellipsis, which sits at the row's right edge;
    // above it when the row is near the foot of the viewport; and never past
    // either side.
    const gap = token("--menu-offset", 4);
    const margin = token("--menu-edge", 8);
    const left = Math.max(margin, Math.min(at.right - size.width, window.innerWidth - size.width - margin));
    let top = at.bottom + gap;
    if (top + size.height > window.innerHeight - margin) top = at.top - gap - size.height;
    // Taller than the room either side: at the top margin, and the
    // stylesheet's `max-height` has it scroll inside its own edge.
    top = Math.max(margin, top);
    node.style.left = `${left}px`;
    node.style.top = `${top}px`;
  }

  const onRelease = () => {
    document.removeEventListener("pointerup", onRelease, true);
    document.removeEventListener("pointercancel", onRelease, true);
    clearTimeout(releasing);
    releasing = setTimeout(() => {
      pressedAnchor = false;
    }, 0);
  };

  // The surface is fixed in the viewport and the document is what scrolls,
  // so a placed menu would stay where its anchor was: re-placed on every
  // scroll — capturing, so a scrolling column counts — and on resize.
  const onMoved = () => {
    if (current) place(current.anchor);
  };
  // And when the document changes around it: a heading renamed from
  // elsewhere wraps to a second line, a row lands above, and the anchor is
  // lower than it was with no scroll to say so. The menu's own repaint and
  // placement are mutations too, and are not a reason to place it again.
  const shifted = new MutationObserver((records) => {
    if (records.every((record) => node.contains(record.target))) return;
    onMoved();
  });

  const onPress = () => {
    if (!current) return;
    clearTimeout(releasing);
    pressedAnchor = true;
    // On the document, capturing: a mouse lifted off the anchor lifts
    // elsewhere, and a cancel is the browser's to send from wherever.
    document.addEventListener("pointerup", onRelease, true);
    document.addEventListener("pointercancel", onRelease, true);
  };

  function open(anchor: HTMLButtonElement, entries: () => MenuItem[]): void {
    // An engine without the Popover API (Safari 16, Firefox 124 and older):
    // the stylesheet already hides the surface, and the press has to land on
    // nothing rather than on a method that is not there.
    if (typeof node.showPopover !== "function") return;
    if (pressedAnchor) {
      pressedAnchor = false;
      close();
      return;
    }
    if (current?.anchor === anchor) {
      close();
      return;
    }
    if (current) close();

    current = { anchor, entries };
    anchor.setAttribute("aria-controls", node.id);
    node.setAttribute("aria-label", anchor.getAttribute("aria-label") ?? "Actions");
    paint(entries());
    anchor.addEventListener("pointerdown", onPress);
    window.addEventListener("scroll", onMoved, true);
    window.addEventListener("resize", onMoved);
    shifted.observe(document.body, { childList: true, subtree: true, attributes: true, characterData: true });
    node.showPopover({ source: anchor });
    place(anchor);
    focusItem(0);
  }

  function close(): void {
    if (!current) return;
    node.hidePopover();
  }

  function refresh(): void {
    if (!current) return;
    const focused = items().findIndex((item) => item === document.activeElement);
    // The anchor's name follows the row's text, which an edit from
    // elsewhere can change under an open menu.
    node.setAttribute("aria-label", current.anchor.getAttribute("aria-label") ?? "Actions");
    paint(current.entries());
    place(current.anchor);
    focusItem(focused < 0 ? 0 : focused);
  }

  // The state work lives here rather than in `open` and `close`, because the
  // browser hides a popover on its own — Escape, a click outside — and
  // `beforetoggle` is the one place every hide passes through. Before, not
  // after: `toggle` is dispatched in a later task, and an anchor that says
  // "expanded" for a frame after the menu has gone is a frame too many.
  node.addEventListener("beforetoggle", (event) => {
    if (!current) return;
    const { newState } = event as ToggleEvent;
    if (newState === "open") {
      current.anchor.setAttribute("aria-expanded", "true");
      return;
    }
    const { anchor } = current;
    // Read before the entries go: a removed element is not the active one.
    const inside = document.activeElement !== null && node.contains(document.activeElement);
    anchor.removeEventListener("pointerdown", onPress);
    window.removeEventListener("scroll", onMoved, true);
    window.removeEventListener("resize", onMoved);
    shifted.disconnect();
    anchor.setAttribute("aria-expanded", "false");
    node.replaceChildren();
    current = undefined;
    // Back to the anchor when the focus was in the menu — Escape, Tab, an
    // entry chosen. Not when a click outside has already put it somewhere.
    // Done here rather than left to the browser, which restores focus to
    // whatever had it before the menu opened: a button a tap does not
    // focus, on some engines, so that would be the page.
    if (inside && anchor.isConnected) anchor.focus({ preventScroll: true });
  });

  node.addEventListener("keydown", (event) => {
    const all = items();
    const at = all.findIndex((item) => item === document.activeElement);
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        focusItem((at + 1) % all.length);
        break;
      case "ArrowUp":
        event.preventDefault();
        focusItem((at - 1 + all.length) % all.length);
        break;
      case "Home":
        event.preventDefault();
        focusItem(0);
        break;
      case "End":
        event.preventDefault();
        focusItem(all.length - 1);
        break;
      case "Tab":
        // Not prevented: the focus is on the anchor by the time the default
        // runs, so the browser's own Tab carries on from there.
        close();
        break;
    }
  });

  return {
    node,
    open,
    close,
    refresh,
    isOpen: () => current !== undefined,
    anchor: () => current?.anchor,
  };
}
